import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import { useAufgabenExercise } from '@/hooks/useAufgabenExercise';
import { useExerciseSound } from '@/hooks/useExerciseSound';
import { useStoreActions, useStoreState } from '@/stores/store';
import {
  RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import api from '../../api/axios';
import encryptedStorage from '../../stores/encryptedStorage';
import { HearingExercise } from '@/components/HearingExercise';
import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import SpeakingExercise from '@/components/SpeakingExercise';
import { WritingExercise } from '@/components/WritingExercise';
import { WritingExerciseSentence } from '@/components/WritingExercise/sentence';
import { isPatientApp } from '@/config/constants';
import { AudioContextProvider } from '@/contexts/AudioContext';
import { SafeAreaView } from 'react-native-safe-area-context';
import { HeaderAufgaben } from './HeaderAufgaben';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { PagerList } from './PagerList';
import { ProgressLayout } from './ProgressLayout';
import { StarProgress } from './StarProgress';
import { Modal } from '@/components/ui/therapist/Modal';
import { Text } from '@/components/Text';
import { PrimaryButton } from '@/components/Button';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { PatientStackParamList } from '@/navigations/PatientStack';
import { StackNavigationProp } from '@react-navigation/stack';
import { Patient, Set } from '@/stores/store.types';
import { styles } from './styles';
import { useExerciseInteractionPanResponder } from '@/hooks/useExerciseInteractionPanResponder';

type ExercisesStackParamList = {
  Exercises: {
    setId: string;
    patientId: string;
    setType: string;
    localWordset: Set;
    isSelf: boolean;
    preview?: boolean;
    selectedPatient?: Patient;
    activeSets: string[];
    activatedExerciseModalities: {
      Lesen: boolean;
      Schreiben: boolean;
      Sprechen: boolean;
      Verstehen: boolean;
    };
  };
};
type ExercisesRouteParams = RouteProp<ExercisesStackParamList, 'Exercises'>;

function Exercises() {
  const [isRecording, setIsRecording] = useState(false);
  const responsiveHeight = useResponsiveHeight(100);
  const isFullscreen = Platform.OS !== 'web' && responsiveHeight < 500;
  const { addListener, dispatch, navigate } =
    useNavigation<
      StackNavigationProp<PatientStackParamList | TherapistStackParamList>
    >();
  const sprechenRef = useRef(null);
  const { params } = useRoute<ExercisesRouteParams>();

  const { showNotification } = useNotification();

  const {
    setId,
    patientId,
    setType,
    localWordset,
    isSelf,
    selectedPatient,
    activeSets,
    activatedExerciseModalities,
    preview,
  } = params;

  const goHome = () => {
    if (isPatientApp) {
      navigate('Home');
    } else {
      navigate('SelfTraining', {
        selectedPatient,
        activeSets,
        preview,
      });
    }
  };

  const goBack = useCallback(
    async (args?: { isNetworkError?: boolean; patientId?: string }) => {
      const { isNetworkError, patientId } = args || {
        isNetworkError: false,
        patientId: params.patientId,
      };
      const exerciseSettings = await encryptedStorage().getItem(setId);
      const data = {
        isNetworkError,
        patientId,
        setId,
        exerciseClass: setType,
        exerciseSettings,
        isSelf,
      };
      navigate('Aufgaben', data);
    },
    [params.patientId, setId, setType, isSelf, navigate],
  );
  const {
    state,
    activeExercise,
    skip,
    next,
    showZielwort,
    isSolved,
    notSolved,
    showResultsModal,
    showResultsModalChange,
    touched, //?  useTimeService return value
    goSelect,
    PAGER_OPTIONS,
  } = useAufgabenExercise({
    setId,
    isTextSet: false,
    patientId,
    localWordsetProp: localWordset,
    goHome,
    isSelfTraining: isSelf,
    activatedExerciseModalities,
    preview,
  });

  const dailyGoalActivated = state.settings?.activateDailyGoal;

  const { panResponder, isTouched } = useExerciseInteractionPanResponder({
    isSelf,
    preview,
    touched,
  });

  const [selectedPerformance, setSelectedPerformance] = useState('none');
  const [peek, setPeek] = useState(false);

  const resultBuffer = useStoreState(
    (state) => state.resultsWithKey['results' + patientId],
  );
  const unloadTherapistBuffer = useStoreActions(
    (actions) => actions.unloadTherapistBuffer,
  );

  const peekIn = useCallback(() => {
    setPeek(true);
  }, []);
  const peekOut = useCallback(() => {
    setPeek(false);
  }, []);

  const changeSelectedPerformance = useCallback((value: string) => {
    return () => {
      setSelectedPerformance((prevState) => {
        if (prevState == value) {
          notSolved();
          return 'none';
        } else {
          isSolved();
          return value;
        }
      });
    };
  }, []);

  const isCorrect = state.nextButton === 'Weiter';
  const { playSound, isPlaying } = useExerciseSound(
    activeExercise?.video ?? activeExercise?.audio,
  );

  const progressBarValue = useMemo(() => {
    if (state.exercises && state.exercises.length > 0) {
      return (state.currentExercise + 1) / state.exercises.length;
    } else {
      return 0;
    }
  }, [state.currentExercise, state.exercises.length]);

  const activeExerciseName = activeExercise?.name;
  const activeExerciseClass = activeExercise?.exerciseClass;

  const isSentenceset = activeExerciseClass == 'sentence';
  let iterationKey = activeExercise
    ? activeExercise.uniqueExercisePrepareDate
    : null;

  const headerFunctions = {
    showZielwort,
    playSound,
    changeSelectedPerformance,
    peekOut,
    peekIn,
  };

  const headerVariableIcons = {
    isPlaying,
  };

  const onSkip = () => {
    if (isCorrect) {
      if (isSelf) {
        next();
      } else {
        sprechenRef.current?.resultCreator?.(null, selectedPerformance);
        next();
      }
    } else {
      skip();
    }
  };

  const unloadResults = useCallback(() => {
    unloadTherapistBuffer({ patientId });
    goBack();
  }, []);

  /*
   * Upload results for Aufgaben
   */
  const uploadResults = useCallback(async () => {
    showResultsModalChange(false);
    try {
      const data = {};
      const buffer = resultBuffer ?? [];
      data.Results = buffer;

      const result = await api.post('/api/results', data);
      if (result.statusText === 'OK' || result.status === 200) {
        showNotification({
          type: 'info',
          title: 'Ergebnisse hochgeladen',
          position: 'top',
        });
        unloadResults();
      }
    } catch (error) {
      if (error?.message === 'Network Error') {
        goBack({ isNetworkError: true, patientId });
      }
    }
  }, [
    resultBuffer,
    showResultsModalChange,
    showNotification,
    unloadResults,
    patientId,
    goBack,
  ]);

  const ActiveComponent = {
    Verstehen: (
      <HearingExercise
        isSolved={isSolved}
        skip={next}
        patientId={patientId}
        exercise={cloneDeep(activeExercise)}
        settings={state.settings}
        ablenkerFromSetJson={state.ablenkerFromSetJson}
        currentExercise={state.currentExercise}
        isSelf={isSelf}
      />
    ),
    Lesen: (
      <HearingExercise
        patientId={patientId}
        isSolved={isSolved}
        skip={next}
        exercise={cloneDeep(activeExercise)}
        settings={state.settings}
        ablenkerFromSetJson={state.ablenkerFromSetJson}
        currentExercise={state.currentExercise}
        isLesen
        isSelf={isSelf}
      />
    ),
    Sprechen: (
      <SpeakingExercise
        ref={sprechenRef}
        patientId={patientId}
        isSolved={isSolved}
        exercise={cloneDeep(activeExercise)}
        settings={state.settings?.Sprechen}
        currentExercise={state.currentExercise}
        selectedPerformance={selectedPerformance}
        changeSelectedPerformance={changeSelectedPerformance}
        isSelf={isSelf}
        isRecordingDisabled={isPlaying}
        setIsRecording={setIsRecording}
        isRecording={isRecording}
        next={next}
      />
    ),
    Schreiben: isSentenceset ? (
      <WritingExerciseSentence
        patientId={patientId}
        isSolved={isSolved}
        exercise={cloneDeep(activeExercise)}
        settings={state.settings?.Schreiben}
        currentExercise={state.currentExercise}
        peek={peek}
        isSelf={isSelf}
        next={next}
      />
    ) : (
      <WritingExercise
        patientId={patientId}
        isSolved={isSolved}
        exercise={cloneDeep(activeExercise)}
        settings={state.settings?.Schreiben}
        currentExercise={state.currentExercise}
        peek={peek}
        isSelf={isSelf}
        next={next}
      />
    ),
  };

  useEffect(() => {
    setSelectedPerformance('none');
  }, [state.currentExercise, iterationKey]);

  useFocusEffect(() => {
    const unsubscribe = addListener('beforeRemove', (e) => {
      e.preventDefault(); // Prevent default action of leaving the screen
      dispatch(e.data.action);
    });
    return unsubscribe;
  });

  return (
    <View style={styles.container}>
      <HeaderAufgaben
        activeExerciseName={activeExerciseName}
        activeExerciseClass={activeExerciseClass}
        headerFunctions={headerFunctions}
        headerVariableIcons={headerVariableIcons}
        onSkip={onSkip}
        isCorrect={isCorrect}
        state={state}
        showResultsModalChange={showResultsModalChange}
        selectedPerformance={selectedPerformance}
        isSelf={isSelf}
        goSelect={isTouched.current ? goSelect : null}
        goHome={goHome}
        isRecording={isRecording}
      />
      {/* Modals */}
      {isPatientApp ? (
        <ConfirmModal
          isVisible={showResultsModal}
          isFullscreen={isFullscreen}
          confirmText={'Speichern'}
          contentText={'Möchten Sie die Ergebnisse dieser Sitzung speichern?'}
          headerText={'Ergebnisse speichern'}
          confirmAction={uploadResults}
          cancelText={'Verwerfen'}
          cancelAction={unloadResults}
        />
      ) : (
        <Modal
          isVisible={showResultsModal}
          title="Ergebnisse speichern"
          confirmActionButton={
            <PrimaryButton
              onPress={uploadResults}
              label="Speichern"
            ></PrimaryButton>
          }
          cancelActionButton={
            <PrimaryButton
              onPress={unloadResults}
              label="Verwerfen"
              look="neutral"
            ></PrimaryButton>
          }
        >
          <Text style={{ fontSize: 16 }}>
            Möchten Sie die Ergebnisse dieser Sitzung speichern?
          </Text>
        </Modal>
      )}
      {/* End of Modals */}
      {activeExercise ? (
        <SafeAreaView style={styles.flex1} {...panResponder.panHandlers}>
          <View style={styles.flex1} key={iterationKey}>
            {ActiveComponent[activeExerciseName]}
          </View>
          <ProgressLayout>
            {isSelf ? (
              <StarProgress
                withStar={dailyGoalActivated}
                progress={
                  dailyGoalActivated ? state.currentProgress : progressBarValue
                }
              />
            ) : (
              <PagerList list={PAGER_OPTIONS} activePager={activeExercise.id} />
            )}
          </ProgressLayout>
        </SafeAreaView>
      ) : (
        <View style={styles.emptyBackground} />
      )}
    </View>
  );
}

export function AufgabenExercise() {
  return (
    <AudioContextProvider>
      <Exercises />
    </AudioContextProvider>
  );
}
